<script>
  import Close from "carbon-icons-svelte/lib/Close.svelte";
  import Checkmark from "carbon-icons-svelte/lib/Checkmark.svelte";
  import Add from "carbon-icons-svelte/lib/Add.svelte";
  import Phone from "carbon-icons-svelte/lib/Phone.svelte";
  import Location from "carbon-icons-svelte/lib/Location.svelte";
  import Building from "carbon-icons-svelte/lib/Building.svelte";
  import { Geocoder } from "@beyonk/svelte-mapbox";
  import { createEventDispatcher, beforeUpdate, onDestroy } from "svelte";
  import { fetchYards, saveYard } from "../../data";
  import {
    inlineAddress,
    parseMapboxAddress,
    portal,
    reportYardImageError,
  } from "../../helpers";
  import YardMaterial from "./YardMaterial.svelte";
  import Contacts from "../Contacts/Contacts.svelte";
  import YardCompany from "./YardCompany.svelte";
  import PhoneInput from "../../components/PhoneInput.svelte";
  import { formatPhoneNumber, toE164 } from "../../phoneUtils.js";

  export let yardId;
  export let yard;

  const dispatch = createEventDispatcher();

  let isSaving = false;
  let saveSuccess = false;
  let saveError = null;
  let saveSuccessTimer;
  let promiseYard;
  let widths = {
    materialNameFieldWidth: 0,
  };

  function close() {
    dispatch("close");
  }

  async function save() {
    isSaving = true;
    saveSuccess = false;
    saveError = null;
    clearTimeout(saveSuccessTimer);
    try {
      if (yard.phone && yard.phone.trim()) {
        yard.phone = toE164(yard.phone.trim());
      }
      if (yard.company && typeof yard.company === 'object') {
        yard.company = {
          ...yard.company,
          name: yard.company.name || yard.name || ""
        };
      } else {
        yard.company = { name: yard.name || "" };
      }
      if (yard.contacts && Array.isArray(yard.contacts)) {
        yard.contacts = yard.contacts.map(c => ({
          ...c,
          name: c.name || "",
          phone: (c.phone && c.phone.trim()) ? toE164(c.phone.trim()) : "",
          email: c.email || "",
          position: c.position || "",
          main: Boolean(c.main)
        }));
      }
      if (yard.accepts && Array.isArray(yard.accepts)) {
        yard.accepts = yard.accepts.map(a => ({
          name: a.name || "",
          details: a.details || null,
          prices: Array.isArray(a.prices) ? a.prices.map(p => ({
            quantity: p.quantity !== undefined && p.quantity !== null && p.quantity !== "" ? (isNaN(p.quantity) ? p.quantity : Number(p.quantity)) : "",
            price: typeof p.price === 'string' && p.price.trim().toLowerCase() === 'free' ? 'free' : (p.price !== "" && !isNaN(p.price) ? Number(p.price) : p.price),
            unit: p.unit === 'bin' ? 'bin' : (p.unit === 'cube' || p.unit === 'cubes') ? 'cubes' : 'tonnes',
            last_updated: p.last_updated || new Date().toISOString(),
            ...(p.last_updated_user ? { last_updated_user: p.last_updated_user } : {})
          })) : []
        }));
      }
      await saveYard(yard);
      isSaving = false;
      saveSuccess = true;
      dispatch("yard-saved", yard);
      dispatch("saved", yard);
      // Window stays open as requested; show temporary saved feedback
      saveSuccessTimer = setTimeout(() => {
        saveSuccess = false;
      }, 3000);
    } catch (err) {
      console.error("Error saving yard:", err);
      isSaving = false;
      saveError = err?.message || "Failed to save yard";
    }
  }

  function handleKeydown(e) {
    if (e.key === "Escape") {
      close();
    }
  }

  let fetched = false;
  beforeUpdate(async () => {
    if (!fetched && yardId) {
      promiseYard = fetchYards(yardId);
      promiseYard.then((value) => {
        if (value && value[0]) {
          yard = value[0];
          if (yard.accepts) {
            yard.accepts = yard.accepts.sort((a, b) =>
              a.name.localeCompare(b.name)
            );
          }
        }
      });
      fetched = true;
    }
  });

  async function onAddressChange(e) {
    let newAddress = e.detail.result;
    yard.address = parseMapboxAddress(newAddress);
  }

  function setNewRow() {
    return {
      name: "",
      details: null,
      prices: [],
    };
  }

  function addRow() {
    yard.accepts = [...(yard.accepts || []), setNewRow()];
  }

  function deleteMaterial(event) {
    let material = event.detail;
    yard.accepts = yard.accepts.filter((accept) => accept.name !== material.name);
  }

  function updateMaterial(event) {
    let material = event.detail;
    let materialIndex = yard.accepts.findIndex(
      (accept) => accept.name === material.name
    );
    if (materialIndex !== -1) {
      yard.accepts[materialIndex] = material;
    }
  }

  onDestroy(() => {
    if (fetched) {
      fetched = false;
    }
    if (saveSuccessTimer) {
      clearTimeout(saveSuccessTimer);
    }
  });
</script>

<svelte:window on:keydown={handleKeydown} />

{#if yard}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <div use:portal class="m3-dialog-scrim" on:click|self={close}>
    <div
      class="m3-dialog-surface"
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog-title"
    >
      <!-- Dialog Header / Top Bar -->
      <div class="m3-dialog-header">
        <div class="header-main-info">
          <div class="header-title-row">
            <h2 id="dialog-title" class="dialog-title">
              {yard.company && yard.company.name
                ? yard.company.name
                : yard.name || "Disposal Facility"}
            </h2>
            <div class="header-badges">
              <span class="m3-id-chip">Yard #{yard.id}</span>
              {#if yard.address && yard.address.suburb}
                <span class="m3-suburb-chip">{yard.address.suburb}</span>
              {/if}
              <span class="m3-count-chip"
                >{(yard.accepts || []).length} Categories</span
              >
            </div>
          </div>
          <span class="dialog-subtitle"
            >Manage facility profile, address, operating entity, and accepted
            material pricing</span
          >
        </div>

        <button
          class="m3-icon-btn close-btn"
          on:click={close}
          title="Close Dialog (Esc)"
          type="button"
        >
          <Close size={20} />
        </button>
      </div>

      <!-- Dialog Body (Two-Column Layout) -->
      <div class="m3-dialog-body">
        <!-- Left Column: Overview, Contacts, Parent Company -->
        <div class="dialog-col-left">
          <!-- Facility Identity Card -->
          <div class="m3-card overview-card">
            <div class="facility-avatar-row">
              <div class="facility-avatar-box">
                {#if yard.image}
                  <img
                    src={"./assests/yards/" + yard.image}
                    alt={yard.name}
                    class="yard-dialog-img"
                    on:error={(e) => {
                      reportYardImageError(
                        yard,
                        "not_found",
                        "./assests/yards/" + yard.image
                      );
                      e.currentTarget.style.display = "none";
                      const fb =
                        e.currentTarget.parentElement.querySelector(
                          ".placeholder-avatar"
                        );
                      if (fb) fb.style.display = "flex";
                    }}
                  />
                  <div
                    class="yard-dialog-img placeholder-avatar"
                    style="display: none;"
                  >
                    🏭
                  </div>
                {:else}
                  <div class="yard-dialog-img placeholder-avatar">🏭</div>
                {/if}
              </div>

              <div class="facility-text-fields">
                <div class="m3-field">
                  <label class="m3-field-label" for="facility-name-input">Operator / Facility Name</label>
                  <div class="m3-input-box">
                    <Building size={16} class="field-icon" />
                    <input
                      id="facility-name-input"
                      type="text"
                      class="m3-input"
                      placeholder="Facility name"
                      bind:value={yard.company.name}
                    />
                  </div>
                </div>

                <div class="m3-field">
                  <label class="m3-field-label" for="direct-phone-input">Direct Phone</label>
                  <PhoneInput
                    id="direct-phone-input"
                    placeholder="02 9999 9999"
                    bind:value={yard.phone}
                  />
                </div>
              </div>
            </div>

            <!-- Facility Address Field -->
            <div class="m3-field address-field">
              <label class="m3-field-label" for="yard-geocoder-box">Facility Address</label>
              <div id="yard-geocoder-box" class="m3-geocoder-wrapper">
                <Location size={16} class="field-icon loc-icon" />
                <div class="geocoder-embed">
                  <Geocoder
                    accessToken={MAPBOX_TOKEN}
                    value={inlineAddress(yard.address)}
                    on:result={onAddressChange}
                    geocoder
                    options={{
                      countries: "AU",
                      placeholder: "Search facility address or suburb...",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Company Contacts Card -->
          <div class="m3-card contacts-card">
            <Contacts bind:contacts={yard.contacts} />
          </div>

          <!-- Parent Operating Entity Card -->
          <YardCompany bind:company={yard.company} on:edit-company />
        </div>

        <!-- Right Column: Accepted Materials & Pricing Tiers -->
        <div class="dialog-col-right">
          <div class="materials-pane-header">
            <div class="materials-pane-title-group">
              <span class="materials-title">Accepted Materials</span>
              <span class="materials-sub"
                >Tipping categories & tiered pricing rates</span
              >
            </div>
            <button class="m3-filled-tonal-btn" on:click={addRow} type="button">
              <Add size={16} />
              <span>Add Category</span>
            </button>
          </div>

          <div class="materials-scroll-container">
            {#if yard.accepts && yard.accepts.length > 0}
              {#each yard.accepts as material (material.name || Math.random())}
                <YardMaterial
                  bind:material={material}
                  bind:widths={widths}
                  on:delete-material={deleteMaterial}
                  on:update-material={updateMaterial}
                />
              {/each}
            {:else}
              <div class="no-materials-box">
                <span class="no-mat-icon">📦</span>
                <span class="no-mat-text"
                  >No material tipping categories configured</span
                >
                <button
                  class="m3-outline-btn"
                  on:click={addRow}
                  type="button"
                >
                  <Add size={14} />
                  <span>Add First Category</span>
                </button>
              </div>
            {/if}
          </div>
        </div>
      </div>

      <!-- M3 Dialog Actions Footer -->
      <div class="m3-dialog-footer">
        <div class="footer-status-indicator">
          {#if saveSuccess}
            <span class="status-dot success-pulse"></span>
            <span class="status-text success-text">✓ Changes saved successfully</span>
          {:else if saveError}
            <span class="status-dot error-dot"></span>
            <span class="status-text error-text">{saveError}</span>
          {:else}
            <span class="status-dot"></span>
            <span class="status-text"
              >Edits will update route calculations and price markers</span
            >
          {/if}
        </div>

        <div class="footer-actions">
          <button class="m3-button m3-btn-cancel" on:click={close} type="button">
            {saveSuccess ? 'Close' : 'Cancel'}
          </button>
          <button
            class="m3-button m3-btn-save {saveSuccess ? 'btn-saved' : ''}"
            on:click={save}
            disabled={isSaving}
            type="button"
          >
            {#if isSaving}
              <span class="spinner-mini"></span>
              <span>Saving...</span>
            {:else if saveSuccess}
              <Checkmark size={16} />
              <span>Saved!</span>
            {:else}
              <Checkmark size={16} />
              <span>Save Changes</span>
            {/if}
          </button>
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  /* Material Design 3 Dialog Scrim */
  .m3-dialog-scrim {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(15, 23, 42, 0.48);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    animation: m3FadeIn 0.2s cubic-bezier(0.2, 0, 0, 1);
  }

  @keyframes m3FadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  /* Material Design 3 Dialog Surface Container */
  .m3-dialog-surface {
    background-color: #ffffff;
    width: 1080px;
    max-width: 95vw;
    height: 88vh;
    max-height: 840px;
    border-radius: 28px;
    box-shadow:
      0 24px 48px -12px rgba(15, 23, 42, 0.22),
      0 12px 24px -6px rgba(15, 23, 42, 0.12),
      0 0 0 1px rgba(0, 0, 0, 0.05);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: m3ScaleIn 0.22s cubic-bezier(0.2, 0, 0, 1);
  }

  @keyframes m3ScaleIn {
    from {
      opacity: 0;
      transform: scale(0.96) translateY(8px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  /* Header / Top Bar */
  .m3-dialog-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 20px 26px 16px;
    background-color: #ffffff;
    border-bottom: 1px solid var(--tt-slate-200);
    flex-shrink: 0;
  }

  .header-main-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  .header-title-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
  }

  .dialog-title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--tt-slate-900);
    letter-spacing: -0.015em;
  }

  .header-badges {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .m3-id-chip {
    font-size: 11.5px;
    font-weight: 700;
    color: var(--tt-primary);
    background-color: rgba(66, 133, 244, 0.1);
    padding: 2px 8px;
    border-radius: 9999px;
  }

  .m3-suburb-chip {
    font-size: 11.5px;
    font-weight: 600;
    color: var(--tt-slate-600);
    background-color: var(--tt-slate-100);
    padding: 2px 8px;
    border-radius: 9999px;
  }

  .m3-count-chip {
    font-size: 11.5px;
    font-weight: 600;
    color: #15803d;
    background-color: #ecfdf5;
    padding: 2px 8px;
    border-radius: 9999px;
    border: 1px solid #bbf7d0;
  }

  .dialog-subtitle {
    font-size: 12px;
    color: var(--tt-slate-500);
  }

  .m3-icon-btn {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: none;
    background: transparent;
    color: var(--tt-slate-500);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.15s ease;
    flex-shrink: 0;
  }

  .m3-icon-btn:hover {
    background-color: var(--tt-slate-100);
    color: var(--tt-slate-900);
  }

  /* Dialog Body */
  .m3-dialog-body {
    flex: 1;
    display: grid;
    grid-template-columns: 1.18fr 1fr;
    gap: 20px;
    padding: 20px 26px;
    overflow-y: auto;
    background-color: #f8fafc;
  }

  .dialog-col-left {
    display: flex;
    flex-direction: column;
    gap: 14px;
    min-width: 0;
  }

  /* M3 Card Container */
  .m3-card {
    background-color: #ffffff;
    border: 1px solid var(--tt-slate-200);
    border-radius: 16px;
    padding: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  }

  .facility-avatar-row {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 12px;
  }

  .facility-avatar-box {
    width: 76px;
    height: 76px;
    border-radius: 14px;
    border: 1px solid var(--tt-slate-200);
    background-color: var(--tt-slate-50);
    overflow: hidden;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  }

  .yard-dialog-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .placeholder-avatar {
    font-size: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f1f5f9;
  }

  .facility-text-fields {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
  }

  /* M3 Text Input Field */
  .m3-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
  }

  .m3-field-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--tt-slate-600);
  }

  .m3-input-box {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
  }

  :global(.m3-input-box .field-icon) {
    position: absolute;
    left: 10px;
    color: var(--tt-slate-400);
    pointer-events: none;
  }

  .m3-input {
    width: 100%;
    height: 36px;
    padding: 0 10px 0 32px;
    font-size: 13px;
    font-weight: 500;
    color: var(--tt-slate-900);
    background-color: #ffffff;
    border: 1px solid var(--tt-slate-300);
    border-radius: 8px;
    outline: none;
    transition: all 0.15s ease;
  }

  .m3-input:focus {
    border-color: var(--tt-primary);
    box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.15);
  }

  /* Geocoder Address Box */
  .m3-geocoder-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
  }

  :global(.m3-geocoder-wrapper .loc-icon) {
    position: absolute;
    left: 10px;
    color: var(--tt-primary);
    z-index: 2;
    pointer-events: none;
  }

  .geocoder-embed {
    width: 100%;
  }

  :global(.geocoder-embed .mapboxgl-ctrl-geocoder) {
    width: 100% !important;
    max-width: 100% !important;
    border: 1px solid var(--tt-slate-300) !important;
    border-radius: 8px !important;
    box-shadow: none !important;
    height: 36px !important;
    min-height: 36px !important;
    padding-left: 24px !important;
    font-size: 13px !important;
    background-color: #ffffff !important;
    transition: all 0.15s ease !important;
  }

  :global(.geocoder-embed .mapboxgl-ctrl-geocoder:focus-within) {
    border-color: var(--tt-primary) !important;
    box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.15) !important;
  }

  :global(.geocoder-embed .mapboxgl-ctrl-geocoder--input) {
    height: 34px !important;
    padding: 6px 30px 6px 8px !important;
    font-size: 12.5px !important;
    color: var(--tt-slate-900) !important;
  }

  :global(.geocoder-embed .mapboxgl-ctrl-geocoder--icon) {
    display: none !important;
  }

  /* Right Column: Materials & Pricing */
  .dialog-col-right {
    display: flex;
    flex-direction: column;
    gap: 12px;
    background-color: #ffffff;
    border: 1px solid var(--tt-slate-200);
    border-radius: 16px;
    padding: 16px;
    height: 100%;
    min-width: 0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  }

  .materials-pane-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--tt-slate-200);
    flex-shrink: 0;
  }

  .materials-pane-title-group {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .materials-title {
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--tt-slate-800);
  }

  .materials-sub {
    font-size: 11px;
    color: var(--tt-slate-500);
  }

  .m3-filled-tonal-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background-color: rgba(66, 133, 244, 0.1);
    color: var(--tt-primary);
    border: 1px solid rgba(66, 133, 244, 0.2);
    border-radius: 9999px;
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .m3-filled-tonal-btn:hover {
    background-color: rgba(66, 133, 244, 0.18);
    transform: translateY(-1px);
  }

  .materials-scroll-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 10px;
    overflow-y: auto;
    padding-right: 4px;
    scrollbar-width: thin;
    scrollbar-color: var(--tt-slate-300) transparent;
  }

  .materials-scroll-container::-webkit-scrollbar {
    width: 5px;
  }

  .materials-scroll-container::-webkit-scrollbar-thumb {
    background: var(--tt-slate-300);
    border-radius: 4px;
  }

  .no-materials-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 36px 16px;
    gap: 8px;
    text-align: center;
    color: var(--tt-slate-400);
  }

  .no-mat-icon {
    font-size: 32px;
  }

  .no-mat-text {
    font-size: 13px;
    font-weight: 500;
  }

  .m3-outline-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: transparent;
    border: 1px solid var(--tt-slate-300);
    border-radius: 9999px;
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 600;
    color: var(--tt-slate-700);
    cursor: pointer;
    margin-top: 4px;
    transition: all 0.15s ease;
  }

  .m3-outline-btn:hover {
    background-color: var(--tt-slate-50);
    border-color: var(--tt-primary);
    color: var(--tt-primary);
  }

  /* M3 Dialog Actions Footer */
  .m3-dialog-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 26px;
    background-color: #ffffff;
    border-top: 1px solid var(--tt-slate-200);
    flex-shrink: 0;
  }

  .footer-status-indicator {
    display: flex;
    align-items: center;
    gap: 7px;
  }

  .status-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background-color: #10b981;
  }

  .status-text {
    font-size: 11.5px;
    color: var(--tt-slate-500);
    font-weight: 500;
  }

  .footer-actions {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .m3-button {
    height: 38px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    border-radius: 9999px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .m3-btn-cancel {
    background: transparent;
    border: 1px solid var(--tt-slate-300);
    color: var(--tt-slate-700);
    padding: 0 20px;
  }

  .m3-btn-cancel:hover {
    background-color: var(--tt-slate-100);
    color: var(--tt-slate-900);
  }

  .m3-btn-save {
    background-color: var(--tt-primary);
    color: #ffffff;
    border: 1px solid transparent;
    padding: 0 24px;
    box-shadow: 0 1px 4px rgba(66, 133, 244, 0.35);
  }

  .m3-btn-save:hover:not(:disabled) {
    background-color: var(--tt-primary-hover);
    box-shadow: 0 2px 8px rgba(66, 133, 244, 0.45);
    transform: translateY(-1px);
  }

  .m3-btn-save.btn-saved {
    background-color: #16a34a;
    box-shadow: 0 1px 4px rgba(22, 163, 74, 0.35);
  }

  .status-dot.success-pulse {
    background-color: #16a34a;
    box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.25);
  }

  .status-text.success-text {
    color: #16a34a;
    font-weight: 600;
  }

  .status-dot.error-dot {
    background-color: #dc2626;
  }

  .status-text.error-text {
    color: #dc2626;
    font-weight: 600;
  }

  .m3-btn-save:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }

  .spinner-mini {
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.4);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
</style>